import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Briefcase,
  Clock,
  Pencil,
  Printer,
  Shield,
  Trash2,
  User as UsuarioIcono,
  UserPlus,
  UsersRound,
} from 'lucide-react';
import { api, type Pagina } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { notificar } from '@/shared/ui/avisos';
import type { PuntoEmision, Rol, Usuario } from '@/shared/api/tipos';
import { useSesion } from '@/shared/sesion/SesionProvider';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { CampoDeBusqueda } from '@/shared/ui/CampoDeBusqueda';
import { ConfirmarEliminacion } from '@/shared/ui/ConfirmarEliminacion';
import { Interruptor } from '@/shared/ui/Interruptor';
import { ListaSeleccionable } from '@/shared/ui/ListaSeleccionable';
import { PanelLateral, CampoDelPanel, claseEntradaPanel } from '@/shared/ui/PanelLateral';
import { Tabla, type Columna } from '@/shared/ui/Tabla';
import { insigniaDeRol, tituloDeRol } from './rolEnPantalla';

const esquema = z.object({
  nombre: z.string().min(1, 'El nombre es obligatorio').max(120),
  correo: z
    .string()
    .min(1, 'El correo es obligatorio')
    .email('El correo no tiene un formato válido'),
  clave: z.union([z.string().min(8, 'Mínimo 8 caracteres').max(72), z.literal('')]),
  rolId: z.coerce.number().int().positive('Elija un rol'),
  cargo: z.string().max(120).optional(),
});

type Formulario = z.infer<typeof esquema>;

export function PaginaUsuarios() {
  const clienteDeConsultas = useQueryClient();
  const { puede } = useSesion();

  const [buscar, setBuscar] = useState('');
  const [pagina, setPagina] = useState(0);
  const [tamanio, setTamanio] = useState(10);
  const [editando, setEditando] = useState<Usuario | null>(null);
  const [creando, setCreando] = useState(false);
  const [aEliminar, setAEliminar] = useState<Usuario | null>(null);
  const [error, setError] = useState<string | null>(null);

  const usuarios = useQuery({
    queryKey: ['usuarios', buscar, pagina, tamanio],
    queryFn: () =>
      api.get<Pagina<Usuario>>('/usuarios', {
        query: { buscar: buscar || undefined, pagina, tamanio },
      }),
  });

  const roles = useQuery({ queryKey: ['roles'], queryFn: () => api.get<Rol[]>('/roles') });

  const puntos = useQuery({
    queryKey: ['puntos-emision'],
    queryFn: () => api.get<PuntoEmision[]>('/puntos-emision'),
    enabled: puede('puntos_emision.ver'),
  });

  const cajaDe = new Map<number, PuntoEmision>(
    (puntos.data ?? []).filter((p) => p.usuarioId !== null).map((p) => [p.usuarioId as number, p]),
  );

  // Cuanta gente hay en cada rol EN TOTAL, no en esta pagina: el numero sale del listado de
  // roles, asi que no miente cuando un rol se parte entre dos paginas.
  const cuantosEnElRol = new Map<string, number>(
    (roles.data ?? []).map((r) => [r.nombre.toLowerCase(), r.usuarios]),
  );

  function refrescar() {
    setError(null);
    void clienteDeConsultas.invalidateQueries({ queryKey: ['usuarios'] });
  }

  function alFallar(causa: unknown, respaldo: string) {
    setError(causa instanceof ErrorDeApi ? causa.message : respaldo);
  }

  const cambiarEstado = useMutation({
    mutationFn: ({ id, activo }: { id: number; activo: boolean }) =>
      api.patch<Usuario>(`/usuarios/${id}`, { activo }),
    onSuccess: refrescar,
    onError: (e) => alFallar(e, 'No se pudo cambiar el estado'),
  });

  const eliminar = useMutation({
    mutationFn: (id: number) => api.delete<void>(`/usuarios/${id}`),
    onSuccess: () => {
      setAEliminar(null);
      refrescar();
      notificar.exito('Usuario eliminado');
    },

    onError: (e) => alFallar(e, 'No se pudo eliminar'),
  });

  const columnas: Columna<Usuario>[] = [
    {
      clave: 'n',
      encabezado: '#',
      clase: 'w-12 pl-5 pr-2',
      contenido: (_, i) => <span className="importe text-armazon-400">{i + 1}</span>,
    },
    {
      clave: 'nombre',
      encabezado: 'Nombre',
      contenido: (u) => (
        <div className="flex items-center gap-2.5">
          <span
            className={`grid size-8 shrink-0 place-items-center rounded-lg ${
              u.administrador ? 'bg-amber-50 text-amber-500' : 'bg-indigo-50 text-indigo-500'
            }`}
          >
            {u.administrador ? (
              <Shield className="size-3.5" aria-hidden />
            ) : (
              <UsuarioIcono className="size-3.5" aria-hidden />
            )}
          </span>
          <span className="font-semibold uppercase text-armazon-800">{u.nombre}</span>
        </div>
      ),
    },
    {
      clave: 'correo',
      encabezado: 'Correo',
      contenido: (u) => <span className="text-xs text-armazon-600">{u.correo}</span>,
    },
    {
      clave: 'cargo',
      encabezado: 'Cargo',
      contenido: (u) =>
        u.cargo ? (
          <span className="text-xs font-medium uppercase text-armazon-600">{u.cargo}</span>
        ) : (
          <Vacio />
        ),
    },
    {
      clave: 'punto',
      encabezado: 'Punto emisión',
      contenido: (u) => {
        const caja = cajaDe.get(u.id);
        if (!caja) {
          return <Vacio />;
        }
        return (

          <span
            title={
              caja.activo
                ? `${caja.identificadorSri} · ${caja.nombre}`
                : `${caja.identificadorSri} · ${caja.nombre} (punto desactivado)`
            }
            className={`inline-flex items-center gap-1.5 text-xs ${
              caja.activo ? '' : 'opacity-50'
            }`}
          >
            <Printer className="size-3 text-armazon-400" aria-hidden />
            <span className="importe font-semibold text-armazon-700">{caja.codigo}</span>
            <span className="uppercase text-armazon-500">{caja.nombre}</span>
          </span>
        );
      },
    },
    {
      clave: 'acceso',
      encabezado: 'Última conexión',
      contenido: (u) =>
        u.ultimoAccesoEn ? (
          <span className="inline-flex items-center gap-1 text-xs uppercase text-armazon-500">
            <Clock className="size-3 text-armazon-400" aria-hidden />
            {new Date(u.ultimoAccesoEn).toLocaleString('es-EC', {
              day: '2-digit',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        ) : (
          <span className="text-xs text-armazon-300">Nunca</span>
        ),
    },
    {
      clave: 'estado',
      encabezado: 'Estado',
      alinear: 'centro',
      clase: 'w-20',
      contenido: (u) => (
        <Interruptor
          activo={u.activo}
          etiqueta={`${u.activo ? 'Desactivar' : 'Activar'} a ${u.nombre}`}
          deshabilitado={cambiarEstado.isPending}
          alCambiar={() => cambiarEstado.mutate({ id: u.id, activo: !u.activo })}
        />
      ),
    },
    {
      clave: 'acciones',
      encabezado: 'Acciones',
      alinear: 'centro',
      clase: 'w-24 pl-2 pr-5',
      contenido: (u) => (
        <div className="flex items-center justify-center gap-1">
          <button
            type="button"
            onClick={() => setEditando(u)}
            title="Editar"
            aria-label={`Editar a ${u.nombre}`}
            className="rounded-lg p-1.5 text-armazon-400 transition-colors hover:bg-blue-50 hover:text-blue-600"
          >
            <Pencil className="size-[15px]" aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => setAEliminar(u)}
            title="Eliminar"
            aria-label={`Eliminar a ${u.nombre}`}
            className="rounded-lg p-1.5 text-armazon-400 transition-colors hover:bg-red-50 hover:text-alerta"
          >
            <Trash2 className="size-[15px]" aria-hidden />
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <EncabezadoDePagina
        icono={UsersRound}
        color="indigo"
        titulo="Usuarios"
        subtitulo="Gestión de usuarios y cajeros del sistema"
        acciones={
          <button
            type="button"
            onClick={() => setCreando(true)}
            title="Nuevo usuario"
            aria-label="Nuevo usuario"
            className="grid size-11 place-items-center rounded-xl bg-marca-600 text-white shadow-sm transition-colors hover:bg-marca-700"
          >
            <UserPlus className="size-5" aria-hidden />
          </button>
        }
      />

      {error && (
        <p
          role="alert"
          className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
        >
          {error}
        </p>
      )}

      <Tabla
        datos={usuarios.data}
        cargando={usuarios.isPending}
        columnas={columnas}
        tamanio={tamanio}
        alCambiarPagina={setPagina}
        alCambiarTamanio={(n) => {
          setTamanio(n);
          setPagina(0);
        }}
        separador={(u, anterior) => {
          if (anterior && anterior.rol === u.rol) {
            return null;
          }
          const cuantos = cuantosEnElRol.get(u.rol.toLowerCase());
          const { icono: Icono, color } = insigniaDeRol(u.rol);
          return (
            <span className="inline-flex items-center gap-1.5">
              <span className={`grid size-4 shrink-0 place-items-center rounded ${color}`}>
                <Icono className="size-2.5" aria-hidden />
              </span>
              <span className="text-armazon-700">
                {tituloDeRol(u.rol)}
              </span>
              {cuantos !== undefined && (
                <span className="text-armazon-400">
                  · {cuantos} {cuantos === 1 ? 'persona' : 'personas'}
                </span>
              )}
            </span>
          );
        }}
        iconoVacio={UsersRound}
        mensajeVacio="Ningún usuario coincide con la búsqueda"
        entidad="usuarios"
        herramientas={
          <CampoDeBusqueda
            valor={buscar}
            alCambiar={(v) => {
              setBuscar(v);
              setPagina(0);
            }}
            marcador="Buscar por nombre o correo…"
            className="w-full max-w-xs"
          />
        }
      />

      <FormularioDeUsuario
        key={editando?.id ?? 'nuevo'}
        abierto={creando || editando !== null}
        usuario={editando}
        roles={roles.data ?? []}
        alCerrar={() => {
          setCreando(false);
          setEditando(null);
        }}
        alGuardar={refrescar}
      />

      <ConfirmarEliminacion
        abierto={aEliminar !== null}
        alCerrar={() => {
          setAEliminar(null);
          setError(null);
        }}
        alConfirmar={() => aEliminar && eliminar.mutate(aEliminar.id)}
        eliminando={eliminar.isPending}
        titulo="Eliminar usuario"
        descripcion="Se retirará el acceso al sistema de:"
        detalle={aEliminar ? `${aEliminar.nombre} · ${aEliminar.correo}` : undefined}
        error={eliminar.isError ? error : null}
      />
    </>
  );
}

function Vacio() {
  return <span className="text-xs text-armazon-300">—</span>;
}

function FormularioDeUsuario({
  abierto,
  usuario,
  roles,
  alCerrar,
  alGuardar,
}: {
  abierto: boolean;
  usuario: Usuario | null;
  roles: Rol[];
  alCerrar: () => void;
  alGuardar: () => void;
}) {
  const editando = usuario !== null;
  const [errorGeneral, setErrorGeneral] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<Formulario>({
    resolver: zodResolver(esquema),

    defaultValues: {
      nombre: usuario?.nombre ?? '',
      correo: usuario?.correo ?? '',
      clave: '',
      rolId: usuario?.rolId ?? 0,
      cargo: usuario?.cargo ?? '',
    },
  });

  const rolElegido = watch('rolId');

  const enviar = handleSubmit(async (datos) => {
    setErrorGeneral(null);
    try {
      const cuerpo: Record<string, unknown> = {
        nombre: datos.nombre,
        correo: datos.correo,
        rolId: datos.rolId,
        cargo: datos.cargo || null,
      };

      if (datos.clave) {
        cuerpo['clave'] = datos.clave;
      }

      if (editando) {
        await api.patch<Usuario>(`/usuarios/${usuario.id}`, cuerpo);
      } else {
        await api.post<Usuario>('/usuarios', cuerpo);
      }

      reset();
      alGuardar();
      alCerrar();
      notificar.exito(`Usuario ${editando ? 'actualizado' : 'guardado'}`);
    } catch (causa) {
      setErrorGeneral(
        causa instanceof ErrorDeApi ? causa.message : 'No se pudo guardar el usuario',
      );
      notificar.error(
        causa instanceof ErrorDeApi ? causa.message : 'No se pudo guardar el usuario',
      );
    }
  });

  return (
    <PanelLateral
      abierto={abierto}
      alCerrar={alCerrar}
      titulo={editando ? 'Editar usuario' : 'Nuevo usuario'}
      descripcion={editando ? usuario.correo : 'Ingrese los datos del nuevo usuario'}
      icono={editando ? Briefcase : UserPlus}
      alEnviar={enviar}
      enviando={isSubmitting}
    >
      {errorGeneral && (
        <p
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900"
        >
          {errorGeneral}
        </p>
      )}

      <CampoDelPanel etiqueta="Nombre" requerido error={errors.nombre?.message}>
        <input {...register('nombre')} className={claseEntradaPanel} autoFocus />
      </CampoDelPanel>

      <CampoDelPanel etiqueta="Correo institucional" requerido error={errors.correo?.message}>
        <input
          type="email"
          autoComplete="off"
          placeholder="nombre@epagal.gob.ec"
          {...register('correo')}
          className={claseEntradaPanel}
        />
      </CampoDelPanel>

      <CampoDelPanel
        etiqueta="Contraseña"
        requerido={!editando}
        error={errors.clave?.message}
        ayuda={editando ? 'Déjela vacía para no cambiarla' : 'Mínimo 8 caracteres'}
      >
        <input
          type="password"
          autoComplete="new-password"
          {...register('clave')}
          className={claseEntradaPanel}
        />
      </CampoDelPanel>

      <CampoDelPanel etiqueta="Cargo" error={errors.cargo?.message}>
        <input placeholder="Recaudadora" {...register('cargo')} className={claseEntradaPanel} />
      </CampoDelPanel>

      <CampoDelPanel
        etiqueta="Rol"
        requerido
        error={errors.rolId?.message}
        ayuda="El usuario tiene un solo rol; sus permisos son los de ese rol"
      >
        <ListaSeleccionable
          nombre="rol"
          valor={rolElegido || null}
          alElegir={(v) => setValue('rolId', v, { shouldValidate: true })}
          vacio="Todavía no hay roles definidos"
          opciones={roles.map((r) => ({
            valor: r.id,
            titulo: tituloDeRol(r.nombre),

            descripcion: r.esDelSistema
              ? 'Acceso total al sistema'
              : `${r.permisos.length} ${r.permisos.length === 1 ? 'permiso' : 'permisos'}`,
            destacada: r.esDelSistema,
          }))}
        />
      </CampoDelPanel>
    </PanelLateral>
  );
}
